import { useEffect } from "react";
import {
  ActivityShopTabs as CoreActivityShopTabs,
  type ActivityShopTabsProps,
} from "@b2bportal/core-activities";
import { CoreActivitiesComponent } from "@b2bportal/core-types";

const HEADER_SELECTOR = ".rbc-layout__header";
const TABS_ID = CoreActivitiesComponent.ActivityShopTabs;

export const ActivityShopTabs = (props: ActivityShopTabsProps) => {
  useEffect(() => {
    const tabsEl = document.getElementById(TABS_ID);
    if (!tabsEl) return;

    const headerEl = document.querySelector(HEADER_SELECTOR);

    // Full-width backdrop: a fixed-position div appended to body.
    // Shown only when tabs are stuck, it provides the full-width white
    // background that can't be achieved via CSS alone due to grid clipping.
    const backdrop = document.createElement("div");
    backdrop.className = "ActivityShopTabs-backdrop";
    backdrop.setAttribute("aria-hidden", "true");
    document.body.appendChild(backdrop);

    // Measure header height and set as CSS custom property for sticky top.
    // Set on the content container (common ancestor of tabs and price section)
    // so both can read the variable. Also positions the backdrop via CSS vars.
    // In webview mode there's no header element — use the tabs' computed top
    // (set via the webview CSS class) as the offset below the native app header.
    const contentContainer = tabsEl.parentElement;
    const getHeaderOffset = () => {
      if (headerEl) return headerEl.getBoundingClientRect().height;
      // Webview: no site header, content starts below the native header
      return 0;
    };
    const updateHeaderHeight = () => {
      if (!tabsEl.isConnected) return;
      const height = getHeaderOffset();
      contentContainer?.style.setProperty("--rbc-header-height", `${height}px`);
      backdrop.style.setProperty("--backdrop-top", `${height}px`);
      backdrop.style.setProperty(
        "--backdrop-height",
        `${tabsEl.offsetHeight}px`
      );
    };

    updateHeaderHeight();

    // Re-measure if header size changes (e.g., fallback header loaded)
    let resizeObserver: ResizeObserver | undefined;
    if (headerEl) {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(headerEl);
    }

    // Stuck detection via scroll listener (throttled with rAF).
    // Toggles the stuck class and shows/hides the full-width backdrop.
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = tabsEl.getBoundingClientRect();
        const headerHeight = getHeaderOffset();
        const isStuck = rect.top <= headerHeight + 1;
        tabsEl.classList.toggle("ActivityShopTabs--stuck", isStuck);
        backdrop.classList.toggle(
          "ActivityShopTabs-backdrop--visible",
          isStuck
        );
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      resizeObserver?.disconnect();
      backdrop.remove();
    };
  }, []);

  return <CoreActivityShopTabs {...props} />;
};
